import React, {
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  type CommunityGalleryWidgetProps,
  type CommunityGalleryThemingProps,
  GALLERY_THEMING_PROP_TO_PARAM,
} from "./types";

/**
 * Ref handle exposed by LegionCommunityGallery for imperative control.
 */
export interface LegionCommunityGalleryHandle {
  /** Reload the widget iframe */
  refresh: () => void;
}

/**
 * Build the iframe src URL from widget props.
 */
export function buildGalleryWidgetUrl(
  props: CommunityGalleryWidgetProps
): string {
  const {
    baseUrl,
    count = 4,
    theme = "light",
    showViewAll = true,
    viewAllUrl = "/gallery",
    communityUrl,
  } = props;

  const url = new URL("/widget/community-gallery", baseUrl);
  url.searchParams.set("count", String(Math.min(Math.max(count, 1), 12)));
  url.searchParams.set("theme", theme);
  url.searchParams.set("showViewAll", String(showViewAll));
  url.searchParams.set("viewAllUrl", viewAllUrl);

  if (communityUrl) {
    url.searchParams.set("communityUrl", communityUrl);
  }

  // Append theming params
  const themingKeys = Object.keys(GALLERY_THEMING_PROP_TO_PARAM) as Array<
    keyof CommunityGalleryThemingProps
  >;
  for (const key of themingKeys) {
    const value = props[key];
    if (value !== undefined && value !== null && String(value).length > 0) {
      url.searchParams.set(GALLERY_THEMING_PROP_TO_PARAM[key], String(value));
    }
  }

  return url.toString();
}

/**
 * LegionCommunityGallery – a React component that embeds the Legion
 * Community Gallery widget via an iframe.
 *
 * @example
 * ```tsx
 * import { LegionCommunityGallery } from '@legionhandtech/lht-react-widgets';
 *
 * function App() {
 *   return (
 *     <LegionCommunityGallery
 *       baseUrl="https://your-legion-domain.com"
 *       count={4}
 *       theme="light"
 *       communityUrl="/community"
 *     />
 *   );
 * }
 * ```
 */
export const LegionCommunityGallery = forwardRef<
  LegionCommunityGalleryHandle,
  CommunityGalleryWidgetProps
>(function LegionCommunityGallery(props, ref) {
  const {
    count = 4,
    theme = "light",
    showViewAll,
    viewAllUrl,
    communityUrl,
    height = "auto",
    className,
    style,
    onLoad,
    onError,
    baseUrl,
    ...rest
  } = props;

  const iframeRef = useRef<HTMLIFrameElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        const iframe = iframeRef.current;
        if (iframe?.contentWindow) {
          iframe.contentWindow.location.reload();
        }
      },
    }),
    []
  );

  const src = useMemo(
    () => buildGalleryWidgetUrl(props),
    [
      baseUrl,
      count,
      theme,
      showViewAll,
      viewAllUrl,
      communityUrl,
      ...Object.keys(GALLERY_THEMING_PROP_TO_PARAM).map(
        (k) => rest[k as keyof typeof rest]
      ),
    ]
  );

  const handleLoad = useCallback(() => {
    onLoad?.({
      widget: "community-gallery",
      count: Math.min(Math.max(count, 1), 12),
      theme,
    });
  }, [count, theme, onLoad]);

  const handleError = useCallback(() => {
    onError?.("Failed to load community gallery widget");
  }, [onError]);

  const resolvedHeight = height === "auto" ? "200px" : height;

  const containerStyle: React.CSSProperties = {
    display: "block",
    width: "100%",
    backgroundColor: "transparent",
    minHeight: "150px",
    height: resolvedHeight,
    ...style,
  };

  const iframeStyle: React.CSSProperties = {
    border: "none",
    borderRadius: "8px",
    overflow: "hidden",
    width: "100%",
    height: resolvedHeight,
  };

  return (
    <div className={className} style={containerStyle}>
      <iframe
        ref={iframeRef}
        src={src}
        title="Community Gallery Widget"
        width="100%"
        loading="lazy"
        style={iframeStyle}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
});

